import { injectable } from "inversify";
import { rootContainer, TYPE } from "../container/root";
import { IApiResponse, IHttpService } from "../http-service";
import {
    IReturnMethodCategory,
    IPlatformLocation,
    ICarrier,
    IAggregatorInfo,
    IReturnMethod,
    LABEL_TYPE,
    IReturnMethodAddress,
    RETURN_METHOD_TYPE,
    IInventoryTrackingConnection,
    IShipmentOptionsForSelectionResponseDto
} from "../../pages/return-methods/return-method-types";
import { ITranslationRecord } from "./translations-service";
import { ILocaleItem } from "../../translation/stores/translations-scope-store";
import { SHIPPING_CONNECTION_CODE } from "../../models/IntegrationsTypes";
import {WithOld} from "../../stores/types";

interface IReturnMethodsData {
    return_methods: IReturnMethodCategory[],
    settings: IReturnMethodsSettings,
}

export interface IReturnMethodsSettings {
    platform_locations_list: IPlatformLocation[],
    aggregators_info: IAggregatorInfo[],
    shipping_api_webhook_base_url?: string,
    should_use_dynamic_address: boolean,
    countries_list: ICountry[],
    inventory_tracking_connections: IInventoryTrackingConnection[],
}

export interface ICountry {
    name: string,
    code: string,
}

interface IReturnMethodsService {
    getReturnMethodsList: () => Promise<IReturnMethodsData>,
}


export interface IDefualtShippingInstructionsArguments {
    type: RETURN_METHOD_TYPE,
    instruction: string,
    address: IReturnMethodAddress|null|undefined,
    aggregatorCode: SHIPPING_CONNECTION_CODE|null|undefined,
    labelType: LABEL_TYPE|null|undefined,
    localeList: ILocaleItem[],
    
}

export interface IRateDTO {
    name?: string,
    carrierCode?: string,
    serviceCode?: string,
    packageCode?: string,
    shipmentCost?: number,
    otherCost?: number,
    currencyCode?: number,
}

@injectable()
class ReturnMethodsService implements IReturnMethodsService {
    private readonly httpService: IHttpService = rootContainer.get<IHttpService>(TYPE.ApiHttp);

    async getReturnMethodsList(): Promise<IReturnMethodsData> {
        const res = await this.httpService.get<IApiResponse<IReturnMethodsData>>(`/rms-api/return-methods`);
        return res.payload;
    }

    async getCarriersList(connectionId: number, useForwardLabel: boolean, address?: IReturnMethodAddress): Promise<ICarrier[]> {
        const res = await this.httpService.post<IApiResponse<ICarrier[]>>(`/rms-api/shipping-label-settings/carriers`, {connectionId: connectionId, address: address, useForwardLabel: useForwardLabel});
        return res.payload;
    }

    async getServiceLevelList(connectionId: number, carrierCode: string, useForwardLabel: boolean, address?: IReturnMethodAddress): Promise<ICarrier[]> {
        const res = await this.httpService.post<IApiResponse<ICarrier[]>>(`/rms-api/shipping-label-settings/carriers/services`, {connectionId: connectionId, carrierCode: carrierCode, address: address, useForwardLabel: useForwardLabel});
        return res.payload;
    }

    async getPackagesList(connectionId: number, carrierCode: string, serviceCode: string): Promise<ICarrier[]> {
        const res = await this.httpService.post<IApiResponse<ICarrier[]>>(`/rms-api/shipping-label-settings/carriers/services/packages`, {connectionId: connectionId, carrierCode: carrierCode, serviceCode: serviceCode});
        return res.payload;
    }

    async updateReturnMethod(returnMethodId: number, returnMethod: WithOld<IReturnMethod>): Promise<IReturnMethod> {
        const res = await this.httpService.put<IApiResponse<IReturnMethod>>(`/rms-api/return-method/${returnMethodId}`, returnMethod);
        return res.payload;
    }

    async deleteReturnMethod(returnMethodId: number): Promise<number> {
        const res = await this.httpService.remove<IApiResponse<number>>(`/rms-api/return-methods/${returnMethodId}`);
        return res.payload;
    }

    async createReturnMethod(returnMethod: IReturnMethod): Promise<IReturnMethod> {
        const res = await this.httpService.post<IApiResponse<IReturnMethod>>(`/rms-api/return-methods`, returnMethod);
        return res.payload;
    }

    async getdefaultShippingInstructions(neededArguments: IDefualtShippingInstructionsArguments): Promise<ITranslationRecord[]> {
        const response = await this.httpService.post<IApiResponse<ITranslationRecord[]>>('/rms-api/return-methods/instructions', neededArguments);
        return response.payload;
    }

    async getRates(returnMethod: IReturnMethod): Promise<IRateDTO[]> {
        const res = await this.httpService.post<IApiResponse<IRateDTO[]>>('/rms-api/shipping-label-settings/get-rates', returnMethod);
        return res.payload;
    }

    async getCarrierSelectionOptionsStore(connectionId: number): Promise<IShipmentOptionsForSelectionResponseDto> {
        const res = await this.httpService.post<IApiResponse<IShipmentOptionsForSelectionResponseDto>>('/rms-api/shipping-label-settings/shipment-options', {connectionId: connectionId});
        return res.payload;
    }
}

export default ReturnMethodsService;